import Image, { ImageOptimProps } from "./ImageOptim"
import Link from "./LinkWrapper"
import React from "react"
import classNames from "classnames"

const Wrapper = ({ onClick, to, ...rest }) => {
  if (onClick) {
    return <button {...rest} onClick={onClick} />
  } else if (to) {
    return <Link to={to} {...rest} />
  } else {
    return <div {...rest} />
  }
}

export interface LinkCardProps {
  /**
   * The class name to apply to the wrapper element
   * */
  className?: string
  /**
   * The description text to display
   * */
  description?: string
  /**
   * The heading text to display
   * */
  heading?: string
  /**
   * The heading element to use
   * */
  headingElement?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6"
  /**
   * The image to display
   * */
  image?: ImageOptimProps | null
  /**
   * The link to navigate to when the card is clicked
   * */
  linkTo?: string
  /**
   * The function to call when the card is clicked
   * */
  onClick?: () => void
  /**
   * The size of the card
   * */
  size?: "compact" | "base" | "standalone"
  /**
   * The title to use for analytics
   * */
  analyticsTitle?: string
  /**
   * An optional label to display metadata, such as a category
   * */
  label?: string
  /**
   * The color scheme of the card
   * */
  linkCardColorScheme?: "light" | "dark"
}

/**
 * A card that links to another page. Often used on landing pages to link to other pages within the section.
 */
const LinkCard = ({
  className,
  description,
  heading,
  image,
  headingElement = "h3",
  linkTo,
  onClick,
  size = "base",
  analyticsTitle,
  label,
  linkCardColorScheme,
}: LinkCardProps) => {
  const Heading = headingElement
  const isClickable = linkTo || onClick

  return (
    <Wrapper
      className={classNames(
        `group border border-gray-300 overflow-hidden focus:outline-none focus:ring`,
        {
          "bg-white": linkCardColorScheme === "light" || !linkCardColorScheme,
          "bg-gray-800": linkCardColorScheme === "dark",
          "flex flex-col": size === `compact` || size === `base`,
          "text-center rounded py-4 px-4": size === `compact`,
          "hover:shadow-md": isClickable && size === `compact`,
          "transition-shadow duration-150": isClickable,
          "text-left rounded-lg": size === `base`,
          "shadow-md hover:shadow-lg":
            (isClickable && size === `base`) ||
            (isClickable && size === `standalone`),
          "text-left flex flex-wrap sm:flex-nowrap rounded-lg w-full":
            size === `standalone`,
        },
        className
      )}
      onClick={onClick}
      to={linkTo}
      data-analytics-region="Link Card"
      data-analytics-title={analyticsTitle ? analyticsTitle : linkTo}
    >
      {/* min-h-1px is needed so IE11 calculates the height properly */}
      <figure
        className={classNames({
          "min-h-1px": size === `compact` || size === `base`,
          "w-24 max-w-full mx-auto": size === `compact`,
          "block w-full": size === `base`,
          "relative sm:w-56 w-full self-stretch sm:shrink-0":
            size === `standalone`,
        })}
      >
        {/* the `standalone` image needs to grow to match the corresponding text
         * column while `compact` and `base` only need to maintain the image’s
         * aspect ratio
         */}
        {size === `standalone` ? (
          <div className="relative h-full">
            <div className="absolute inset-0">
              <Image
                {...image}
                cropRatio={0.525}
                cropWidth={568}
                suppressCaption={true}
                cover={true}
              />
            </div>
            <canvas
              className={`w-full max-w-full h-auto block`}
              width={size === `compact` ? 96 : 1200}
              height={size === `compact` ? 48 : 630}
            />
          </div>
        ) : (
          <Image
            {...image}
            cropRatio={size === `compact` ? 0.5 : 0.525}
            cropWidth={size === `compact` ? 96 : 568}
            suppressCaption={true}
          />
        )}
      </figure>
      <div
        className={classNames(`space-y-1`, {
          "flex flex-col flex-auto": size === `compact` || size === `base`,
          "px-4 pb-6 pt-4": size === `base`,
          "pt-2": size === `compact`,
          "p-4 pb-5 sm:p-6": size === `standalone`,
        })}
      >
        {heading && (
          <Heading
            className={classNames({
              "text-gray-900":
                linkCardColorScheme === "light" || !linkCardColorScheme,
              "text-white": linkCardColorScheme === "dark",
              "group-hover:text-gray-700":
                isClickable &&
                (linkCardColorScheme === "light" || !linkCardColorScheme),
              "group-hover:text-gray-400":
                isClickable && linkCardColorScheme === "dark",
              "text-sm font-bold text-gray-900 leading-snug":
                size === `compact`,
              "text-lg": size === `base`,
              "font-bold leading-snug duration-150 transition-colors":
                size === `base` || size === `standalone`,
              "text-sm sm:text-lg": size === `standalone`,
            })}
          >
            {heading}
            {isClickable && (size === `base` || size === `standalone`) ? (
              <i
                className={`icon-chevron-right`}
                style={{ width: `18px`, marginRight: `-18px` }}
              ></i>
            ) : null}
          </Heading>
        )}
        {description && (
          <p
            className={classNames("leading-snug", {
              "text-gray-700":
                linkCardColorScheme === "light" || !linkCardColorScheme,
              "text-white": linkCardColorScheme === "dark",
              "text-xs": size === `compact`,
              "text-sm": size === `base`,
              "text-sm sm:text-base": size === `standalone`,
            })}
          >
            {description}
          </p>
        )}
        {label && (
          <div className={`flex flex-auto flex-col justify-end pt-4`}>
            <p
              className={`text-gray-700 font-bold text-xs uppercase leading-tight`}
            >
              {label}
            </p>
          </div>
        )}
      </div>
    </Wrapper>
  )
}

export default LinkCard
